import React, { useState, useEffect } from 'react';
import axios from 'axios';
import logo from '../images/logo.png';
import { useLocation, useNavigate } from 'react-router-dom';
import ReCAPTCHA from 'react-google-recaptcha';
import 'bootstrap-icons/font/bootstrap-icons.css';
import './css/Login.css';

const API_URL =
  process.env.REACT_APP_API_URL ||
  (process.env.NODE_ENV === 'development' ? 'http://localhost:5000/api' : '/api');

const UserLogin = ({ handleLogin }) => {
  const location = useLocation();
  const [formData, setFormData] = useState({ email: location.state?.email || '', password: '' });
  const [message, setMessage] = useState('');
  const [captchaValue, setCaptchaValue] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const navigate = useNavigate();

  // Resume session only when a JWT is present (API calls require Bearer token).
  useEffect(() => {
    const token = localStorage.getItem('token');
    const personId = localStorage.getItem('personId');
    const username = localStorage.getItem('username');
    const role = localStorage.getItem('role');
    if (token && personId && username && role === 'user') {
      handleLogin({ token, personId, username, role });
      navigate('/user/dashboard', { replace: true });
    }
  }, [navigate, handleLogin]);

  const handleChange = (e) => setFormData({ ...formData, [e.target.name]: e.target.value });
  const handleCaptchaChange = (value) => setCaptchaValue(value);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!captchaValue) {
      setMessage('Please complete the reCAPTCHA');
      return;
    }

    setIsSubmitting(true);

    try {
      const res = await axios.post(`${API_URL}/auth/user-login`, {
        ...formData,
        captcha: captchaValue,
      });

      const { token, username, role, personId, refreshToken } = res.data;

      if (!token) {
        setMessage('Login failed — token not received.');
        return;
      }

      localStorage.setItem('token', token);
      localStorage.setItem('personId', personId || '');
      localStorage.setItem('username', username || '');
      localStorage.setItem('role', role || 'user');
      if (refreshToken) {
        localStorage.setItem('refreshToken', refreshToken);
      }

      sessionStorage.setItem('token', token);
      sessionStorage.setItem('personId', personId || '');
      sessionStorage.setItem('username', username || '');
      sessionStorage.setItem('role', role || 'user');
      sessionStorage.removeItem('impersonating');

      handleLogin({ token, personId, username, role: role || 'user' });
      setMessage('Login successful');
      navigate('/user/dashboard', { replace: true });
    } catch (err) {
      setMessage(err?.response?.data?.msg || 'Invalid credentials');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="login-page bk-img">
      <div className="form-content">
        <div className="container">
          <div className="row d-flex align-items-center justify-content-center vh-100">
            <div className="col-md-6 login-box">
              <h1 className="text-center pt-2x">
                <img src={logo} width="150" alt="Logo" />
              </h1>
              <h1 className="text-center text-bold">User Login</h1>
              <div className="well row pt-2x pb-3x bk-light">
                <div className="col-md-8 offset-md-2">
                  <form onSubmit={handleSubmit}>
                    <div className="form-group mb-3">
                      <input
                        type="email"
                        name="email"
                        className="form-control"
                        placeholder="Enter your email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                      />
                    </div>

                    <div className="form-group mb-3 position-relative">
                      <input
                        type={showPassword ? 'text' : 'password'}
                        name="password"
                        className="form-control pe-5"
                        placeholder="Enter your password"
                        value={formData.password}
                        onChange={handleChange}
                        required
                      />
                      <i
                        className={`bi ${showPassword ? 'bi-eye' : 'bi-eye-slash'}
                        position-absolute top-50 end-0 translate-middle-y me-3`}
                        style={{ cursor: 'pointer', fontSize: '1.2rem' }}
                        onClick={() => setShowPassword(!showPassword)}
                      />
                    </div>

                    <div className="login-recaptcha-wrapper">
                      <ReCAPTCHA
                        sitekey={process.env.REACT_APP_RECAPTCHA_SITE_KEY || 'your_site_key_here'}
                        onChange={handleCaptchaChange}
                      />
                    </div>

                    <button type="submit" className="btn btn-warning btn-block mt-3" disabled={isSubmitting}>
                      {isSubmitting ? 'Logging in...' : 'Login'}
                    </button>

                    {message && (
                      <div className={`alert mt-3 ${message === 'Login successful' ? 'alert-success' : 'alert-danger'}`}>
                        {message}
                      </div>
                    )}

                    <div className="text-center mt-3">
                      <a href="/user-forgot-password">Forgot password?</a>
                    </div>
                  </form>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UserLogin;
